/** Minesweeper rules (C). Each board owns its cells; no DOM, clock, or shared mutable state. */
export const DIFFICULTIES = [
    { key: 'beginner', game: 'minesweeper', rows: 9, cols: 9, mines: 10 },
    { key: 'intermediate', game: 'minesweeperIntermediate', rows: 16, cols: 16, mines: 40 },
    { key: 'expert', game: 'minesweeperExpert', rows: 16, cols: 30, mines: 99 },
];

export function createGame({ difficulty = 'beginner', rng = Math.random } = {}) {
    const config = DIFFICULTIES.find(item => item.key === difficulty) || DIFFICULTIES[0];
    return {
        ...config,
        rng,
        cells: Array.from({ length: config.rows * config.cols }, () => ({
            mine: false, adjacent: 0, revealed: false, flagged: false,
        })),
        status: 'ready',
        armed: false,
        flags: 0,
        revealed: 0,
        exploded: null,
    };
}

export function neighbours(state, index) {
    const row = Math.floor(index / state.cols);
    const col = index % state.cols;
    const result = [];
    for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
            const y = row + dy;
            const x = col + dx;
            if ((dx !== 0 || dy !== 0) && y >= 0 && y < state.rows && x >= 0 && x < state.cols) {
                result.push(y * state.cols + x);
            }
        }
    }
    return result;
}

function arm(state, first) {
    const safe = new Set([first, ...neighbours(state, first)]);
    const candidates = state.cells.map((_, i) => i).filter(i => !safe.has(i));
    for (let i = candidates.length - 1; i > 0; i -= 1) {
        const j = Math.floor(state.rng() * (i + 1));
        [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
    }
    candidates.slice(0, state.mines).forEach(i => { state.cells[i].mine = true; });
    state.cells.forEach((cell, i) => {
        cell.adjacent = neighbours(state, i).filter(n => state.cells[n].mine).length;
    });
    state.armed = true;
    state.status = 'playing';
}

function openCells(state, indexes) {
    const queue = [...indexes];
    while (queue.length > 0 && state.status === 'playing') {
        const index = queue.pop();
        const cell = state.cells[index];
        if (cell.revealed || cell.flagged) continue;
        cell.revealed = true;
        if (cell.mine) {
            state.status = 'lost';
            state.exploded = index;
            break;
        }
        state.revealed += 1;
        if (cell.adjacent === 0) queue.push(...neighbours(state, index));
    }
    if (state.status === 'playing' && state.revealed === state.cells.length - state.mines) {
        state.status = 'won';
        state.cells.forEach((cell) => { if (cell.mine) cell.flagged = true; });
        state.flags = state.mines;
    }
}

export function chord(state, index) {
    const cell = state.cells[index];
    if (state.status !== 'playing' || !cell?.revealed || cell.adjacent === 0) return state;
    const around = neighbours(state, index);
    if (around.filter(i => state.cells[i].flagged).length === cell.adjacent) openCells(state, around);
    return state;
}

export function reveal(state, index) {
    const cell = Number.isInteger(index) && state.cells[index];
    if (!cell || cell.flagged || state.status === 'won' || state.status === 'lost') return state;
    if (cell.revealed) return chord(state, index);
    if (!state.armed) arm(state, index);
    openCells(state, [index]);
    return state;
}

export function toggleFlag(state, index) {
    const cell = Number.isInteger(index) && state.cells[index];
    if (!cell || cell.revealed || state.status === 'won' || state.status === 'lost') return state;
    cell.flagged = !cell.flagged;
    state.flags += cell.flagged ? 1 : -1;
    return state;
}
